/** Typed evaluated Insight data, shared by API responses and frozen document blocks. */
import { z } from 'zod';
import { FilterNodeSchema } from '../filter';
import { InsightBucket, InsightViz } from './insight';

/** How a measure's values are written (`auto` resolved from the field: money with its currency, …). */
export const ResolvedFormat = z.object({
  kind: z.enum(['number', 'money', 'percent', 'duration']),
  currency: z.string().optional(),
  decimals: z.number().int().min(0).max(6).optional(),
  compact: z.boolean().optional(),
});
export type ResolvedFormat = z.infer<typeof ResolvedFormat>;

export const DimensionKind = z.enum(['time', 'choice', 'user', 'record', 'boolean', 'date']);
export type DimensionKind = z.infer<typeof DimensionKind>;

export const InsightResultRow = z.object({
  /** One key per dimension: option key, member id, record id, `true`/`false`, bucket start `YYYY-MM-DD`; null = empty. */
  keys: z.array(z.string().nullable()),
  /** Display labels in the member's language (option labels, names, titles, `Sep 2026`, `Empty`). */
  labels: z.array(z.string()),
  /** Colours of option keys (`#12B76A`, token names), else null. */
  colors: z.array(z.string().nullable()).optional(),
  /** One value per measure. */
  values: z.array(z.number().nullable()),
  /** Formatted field values for record lists (dates, choices, text, money). */
  displayValues: z.array(z.string()).optional(),
  /** Values of the comparison window (same bucket position). */
  compare: z.array(z.number().nullable()).optional(),
  /** Records in this row. */
  count: z.number().int(),
  /** The "Other" row of a top-N dimension. */
  other: z.boolean().optional(),
});
export type InsightResultRow = z.infer<typeof InsightResultRow>;

export const InsightResult = z.object({
  viz: InsightViz,
  object: z.object({ key: z.string(), label: z.string(), labelSingular: z.string() }),
  measures: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      fn: z.string(),
      format: ResolvedFormat,
    }),
  ),
  dimensions: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      field: z.array(z.string()),
      kind: DimensionKind,
      bucket: InsightBucket.optional(),
    }),
  ),
  rows: z.array(InsightResultRow),
  totals: z.object({
    values: z.array(z.number().nullable()),
    compare: z.array(z.number().nullable()).optional(),
    count: z.number().int(),
  }),
  goal: z
    .object({ value: z.number().nullable(), label: z.string().optional(), per: z.enum(['period', 'bucket']) })
    .optional(),
  goodWhen: z.enum(['up', 'down']).optional(),
  window: z
    .object({
      from: z.string(),
      to: z.string(),
      label: z.string(),
      compare: z.object({ from: z.string(), to: z.string(), label: z.string() }).optional(),
    })
    .optional(),
  /**
   * Only on an empty window: how many records the source has outside it, and the day of the latest
   * one (`YYYY-MM-DD`), so "nothing in Q4" can say "latest Sep 2026" instead of "no records".
   */
  outside: z.object({ count: z.number().int(), latest: z.string().nullable() }).optional(),
  /** Records behind the insight (source filter, host filters and window) for "View N records". */
  drill: z.object({
    enabled: z.boolean(),
    object: z.string(),
    filter: FilterNodeSchema.nullable(),
    timeField: z.array(z.string()).optional(),
  }),
  /** More groups exist than were returned. */
  truncated: z.boolean(),
  computedAt: z.string(),
  cached: z.boolean(),
});
export type InsightResult = z.infer<typeof InsightResult>;
